import {
  FETCH_USER_START,
  FETCH_USER_SUCCESS,
  FETCH_USER_ERROR,
  REGISTER_USER_START,
  REGISTER_USER_SUCCESS,
  REGISTER_USER_ERROR,
  LOGOUT,
  RESET_NEW_DATASOURCE,
  UPDATE_USER_ATTRIBUTES_START,
  UPDATE_USER_ATTRIBUTES_SUCCESS,
  UPDATE_USER_ATTRIBUTES_ERROR,
  PATCH_ORG_ATTRIBUTES_START,
  PATCH_ORG_ATTRIBUTES_SUCCESS,
  PATCH_ORG_ATTRIBUTES_ERROR,
} from '../actions/types';
import type Organization from '../types/Organization';
import type { FullPermission } from '../types/Role';
import type SinatraState from '../types/SinatraState';
import persistFactory from './persistFactory';
import type { PersistOptions } from './persistFactory';

type SinatraUser = SinatraState['user'];

/** The profile shape `/user` returns once a fetch succeeds. */
type UserProfile = NonNullable<SinatraUser['profile']>;

type CurrentRole = NonNullable<SinatraUser['currentRole']>;

/**
 * Organization attributes are written back verbatim from the API under a key
 * named after the application, so on this write path the bag is unstructured.
 * `Organization` declares only the two application keys the UI reads by name,
 * and a value under an arbitrary key cannot be assigned to those.
 */
type OrgAttributeBag = Record<string, Record<string, unknown> | undefined>;

/**
 * An organization as this reducer stores it. The `/user` payload is kept as-is
 * and the attribute patches below rewrite part of it, so nothing on this path
 * guarantees the full `Organization` shape.
 */
export type StoredOrganization = Partial<Omit<Organization, 'attributes'>> & {
  attributes?: OrgAttributeBag;
};

export interface OrgAttributesPayload {
  orgID: number;
  applicationName: string;
  data: Record<string, unknown>;
}

export interface UserErrors {
  loading: string | null;
  registering: string | null;
  updatingUserAttrs: string | null;
  updatingOrgAttrs: string | null;
}

/**
 * The user slice as this reducer actually produces it, which is not quite what
 * `SinatraState['user']` declares: `profile`, `currentOrg` and `currentRole`
 * start empty and only fill in after a successful fetch; `errors`,
 * `newDataSourceList` and `updatingUserAttrs` exist here but are missing from
 * `SinatraState`; and `isRegistering` is declared there although no case sets
 * it. Reconciling the two is its own change — see SinatraState.tsx.
 */
export interface UserState
  extends Omit<
    SinatraUser,
    'profile' | 'currentOrg' | 'currentRole' | 'isRegistering'
  > {
  profile: Partial<Omit<UserProfile, 'organizations'>> & {
    organizations?: StoredOrganization[];
  };
  currentOrg: StoredOrganization;
  currentRole: Partial<CurrentRole>;
  newDataSourceList: FullPermission[];
  updatingUserAttrs?: boolean;
  errors: UserErrors;
}

export type UserAction =
  | { type: typeof FETCH_USER_START }
  | {
      type: typeof FETCH_USER_SUCCESS;
      profile: UserState['profile'];
      currentOrg: UserState['currentOrg'];
      currentRole: UserState['currentRole'];
      hasNewDataSource: boolean;
      newDataSourceList: FullPermission[];
      pageAccessList: SinatraUser['pageAccessList'];
    }
  | { type: typeof FETCH_USER_ERROR; error: string }
  | { type: typeof REGISTER_USER_START }
  | { type: typeof REGISTER_USER_SUCCESS; payload: UserState['profile'] }
  | { type: typeof REGISTER_USER_ERROR; error: string }
  // The creator reads the acknowledgement out of localStorage and forwards the
  // raw value, so a real dispatch can carry a string here even though this and
  // `hasNewDataSource` are both declared boolean. Fixing the creator is a
  // behavioral change and belongs in its own commit.
  | { type: typeof RESET_NEW_DATASOURCE; isAcknowledged: boolean }
  | { type: typeof UPDATE_USER_ATTRIBUTES_START }
  | {
      type: typeof UPDATE_USER_ATTRIBUTES_SUCCESS;
      payload: { applicationName: string; data: Record<string, unknown> };
    }
  | { type: typeof UPDATE_USER_ATTRIBUTES_ERROR }
  | { type: typeof PATCH_ORG_ATTRIBUTES_START }
  | { type: typeof PATCH_ORG_ATTRIBUTES_SUCCESS; payload: OrgAttributesPayload }
  | { type: typeof PATCH_ORG_ATTRIBUTES_ERROR }
  | { type: typeof LOGOUT };

export const initialState: UserState = {
  profile: {},
  isLoggedIn: false,
  loading: false,
  registering: false,
  currentOrg: {},
  currentRole: {},
  hasNewDataSource: false,
  newDataSourceList: [],
  pageAccessList: [],
  errors: {
    loading: null,
    registering: null,
    updatingUserAttrs: null,
    updatingOrgAttrs: null,
  },
};

export function updateOrgAttrs(
  orgs: StoredOrganization[],
  payload: OrgAttributesPayload,
): StoredOrganization[] {
  const { orgID, applicationName, data } = payload;
  return orgs.map(org => {
    if (org.id === orgID) {
      return {
        ...org,
        attributes: {
          [applicationName]: data,
        },
      };
    }
    return org;
  });
}

export function patchOrgAttrs(
  org: StoredOrganization,
  payload: OrgAttributesPayload,
): StoredOrganization {
  const { orgID, applicationName, data } = payload;
  if (org.id === orgID) {
    const existing = org.attributes?.[applicationName];
    return {
      ...org,
      attributes: {
        [applicationName]: existing ? { ...existing, ...data } : data,
      },
    };
  }
  return org;
}

export function patchOrgsAttrs(
  orgs: StoredOrganization[],
  payload: OrgAttributesPayload,
): StoredOrganization[] {
  return orgs.map(org => patchOrgAttrs(org, payload));
}

/**
 * Exported unwrapped so tests exercise the reducer against `UserAction` and
 * `UserState`. The default export is the same reducer behind redux-persist,
 * whose signature widens the action back to redux's base `Action`.
 */
export function userReducer(
  state: UserState = initialState,
  action: UserAction,
): UserState {
  switch (action.type) {
    case FETCH_USER_START:
      return {
        ...state,
        loading: true,
      };
    case FETCH_USER_SUCCESS: {
      return {
        ...state,
        loading: false,
        isLoggedIn: true,
        profile: action.profile,
        currentRole: action.currentRole,
        currentOrg: action.currentOrg,
        hasNewDataSource: action.hasNewDataSource,
        newDataSourceList: action.newDataSourceList,
        pageAccessList: action.pageAccessList,
      };
    }
    case FETCH_USER_ERROR:
      return {
        ...state,
        loading: false,
        isLoggedIn: false,
        errors: { ...state.errors, loading: action.error },
      };
    case REGISTER_USER_START:
      return {
        ...state,
        registering: true,
      };
    case REGISTER_USER_SUCCESS:
      return {
        ...state,
        registering: false,
        profile: action.payload,
      };
    case REGISTER_USER_ERROR:
      return {
        ...state,
        registering: false,
        errors: {
          ...state.errors,
          registering: action.error,
        },
      };
    case RESET_NEW_DATASOURCE:
      return {
        ...state,
        hasNewDataSource: action.isAcknowledged,
      };
    case UPDATE_USER_ATTRIBUTES_START:
      return {
        ...state,
        updatingUserAttrs: true,
      };
    case UPDATE_USER_ATTRIBUTES_SUCCESS:
      return {
        ...state,
        updatingUserAttrs: false,
        profile: {
          ...state.profile,
          attributes: {
            [action.payload.applicationName]: {
              ...state.profile.attributes?.[action.payload.applicationName],
              ...action.payload.data,
            },
          },
        },
      };
    case UPDATE_USER_ATTRIBUTES_ERROR:
      return {
        ...state,
        updatingUserAttrs: false,
      };
    case PATCH_ORG_ATTRIBUTES_START:
      return {
        ...state,
      };
    case PATCH_ORG_ATTRIBUTES_SUCCESS: {
      const { organizations } = state.profile;
      return {
        ...state,
        currentOrg: patchOrgAttrs(state.currentOrg, action.payload),
        // No organizations means nothing to patch; leaving the profile as it is
        // beats writing an organizations key the rest of the app would then
        // find empty.
        profile: organizations
          ? {
              ...state.profile,
              organizations: patchOrgsAttrs(organizations, action.payload),
            }
          : state.profile,
      };
    }
    case PATCH_ORG_ATTRIBUTES_ERROR:
      return {
        ...state,
      };
    case LOGOUT:
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

const opts: PersistOptions<UserState> = {
  whitelist: [
    'currentOrg',
    'currentRole',
    'profile',
    'isLoggedIn',
    'hasNewDataSource',
    'newDataSourceList',
    'pageAccessList',
  ],
};

export default persistFactory('user', userReducer, opts);
